import { IButtonProps } from "@/template/components/Button";
import styled, { css, DefaultTheme } from "styled-components";

export const FlipButtonContainer = styled.button`
  perspective: 1000px;
  min-height: 48px;
  font-size: 18px;
  width: 200px;
  height: 48px;
  border: none;
  background-color: transparent;
  cursor: pointer;

  ${({ theme }) => theme.breakpoints.sm.query} {
    width: 100%;
  }
`;

export const FlipButtonInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transition: transform 0.3s ease-in-out; /* Added ease-in-out for better feel */
  transform-style: preserve-3d;

  ${FlipButtonContainer}:hover & {
    transform: rotateX(180deg);
  }
`;

interface IButtonColors {
  backgroundColor: string;
  backgroundHoverColor: string;
  color: string; // resting text color
  fontHoverColor: string; // hover text color
  borderColor: string; // resting border color
  borderHoverColor: string; // hover border color
}

const getButtonColors = (
  variant: IButtonProps["variant"],
  colorKey: IButtonProps["color"],
  theme: DefaultTheme
): IButtonColors => {
  let backgroundColor = "";
  let backgroundHoverColor = "";
  let color = ""; // Default text color fallback
  let fontHoverColor = "";
  let borderColor = "";
  let borderHoverColor = "";

  const colorStore = theme.colors?.[colorKey || ""] || {
    default: theme.colors?.base,
    100: theme.colors?.base,
    200: theme.colors?.base,
    300: theme.colors?.base,
    400: theme.colors?.base,
  };

  switch (variant) {
    case "solid":
      if (color === "default") {
        backgroundColor = colorStore[0];
        backgroundHoverColor = colorStore[100];
        borderColor = colorStore[100];
        color = colorStore.default;
      } else {
        backgroundColor = colorStore.default;
        backgroundHoverColor =
          colorStore[400] === colorStore.default
            ? colorStore[300]
            : colorStore[400];
        color = theme.colors?.base;
      }
      break;
    case "flat":
      backgroundColor = colorStore[50];
      backgroundHoverColor = colorStore[100];
      color = colorStore.default;
      break;
    case "outline":
      if (color === "default") {
        borderColor = colorStore[300];
        backgroundHoverColor = colorStore[100];
        color = colorStore.default;
      } else {
        borderColor = colorStore.default;
        backgroundHoverColor = colorStore[100];
        color = colorStore.default;
      }
      break;

    case "light":
      backgroundColor = "transparent";
      backgroundHoverColor = colorStore[100];
      color = colorStore.default;
      break;
  }

  return {
    backgroundColor,
    backgroundHoverColor,
    color,
    fontHoverColor,
    borderColor,
    borderHoverColor,
  };
};

export const FlipFace = styled.div<{
  $variant: IButtonProps["variant"];
  $color: IButtonProps["color"];
}>`
  ${({ $variant, $color, theme }) => {
    const colors = getButtonColors($variant, $color, theme);

    const { backgroundColor, color, borderColor } = colors;
    return css`
      position: absolute;
      width: 100%;
      height: 100%;

      backface-visibility: hidden;
      background: ${backgroundColor};
      color: ${color};
      box-shadow: inset 0 0 0 1px ${borderColor || backgroundColor};

      display: flex;
      align-items: center;
      gap: 8px;
      justify-content: center;
      font-size: 18px;
      font-weight: 600;
      border-radius: 8px;
    `;
  }}
`;

export const FlipButtonFront = styled(FlipFace)`
  transform: rotateX(0deg);
`;

export const FlipButtonBack = styled(FlipFace)`
  transform: rotateX(180deg);
  ${({ $variant, $color, theme }) => {
    const colors = getButtonColors($variant, $color, theme);

    const {
      backgroundHoverColor,
      fontHoverColor,
      borderHoverColor,
      backgroundColor,
    } = colors;
    return css`
      background: ${backgroundHoverColor};
      color: ${fontHoverColor};
      box-shadow: inset 0 0 0 1px
        ${borderHoverColor || backgroundHoverColor || backgroundColor};
    `;
  }}
`;
